import { getRangeBySight } from './../../Grid/scripts/helpers/getRangeByAttack'
import {
  ActionAttackTo,
  ActionHitReceived,
  ActionDie,
  ActionInvalid,
  ActionGoTo,
  ActionLoot,
} from './../../../f.assets/Characters/scripts/classes/CharacterActions'
import { CHARACTER_VARIANTS, WEAPONS } from './../../../f.assets/Characters/scripts/constants'
import { Object3D, Vector2 } from 'three'
import { ActionAwait, CharacterAction } from '../../../f.assets/Characters/scripts/classes/CharacterActions'
import { CHARACTER_ACTION, CHARACTER_STATES } from '../../../f.assets/Characters/scripts/constants'
import { addEventListener, EVENTS_TICK } from '../../../b.providers/EventsProvider/EventsProvider'
import { GAME_OBJECTS } from './types'
import { Characters } from '../../../d.controllers/CharactersController'
import { COMMANDERS } from '../../../c.managers/LevelManager/store.d'
import Character from './Character'
import replaceRedMatCapMaterial, {
  restoreFromRedMatCapMaterials,
} from '../../../f.assets/scripts/materialReplacement/replaceRedMatCapMaterial'
import replaceAsphaltMaterial, {
  restoreFromAsphaltMaterials,
} from '../../../f.assets/scripts/materialReplacement/replaceAsphaltMaterial'
import MinionActionPerformer from '../helpers/MinionActionPerformer'
import getObjectsInRange from '../../Grid/scripts/helpers/getObjectsInRange'
import { Debug } from '../../../d.controllers/Debug'
import getGameElementByUUID from '../helpers/getGameElementByUUID'
import Candy from './Candy'
import { Terrain } from '../../../d.controllers/TerrainController'
import { Battle } from '../../../d.controllers/BattleController'
import getEnemyTreasure from '../../Grid/scripts/helpers/getEnemyTreasure'

export default class Minion extends Character {
  unsub: () => void
  // actions queue
  actions: CharacterAction[]
  // ID of the chasing character
  targetUUID?: string
  // the coords in terrain that the minion respawn from
  plot: Vector2
  // var to store method that restores material after being affected by a mutation
  restoreMutation?: (obj: Object3D) => void
  mutation: CHARACTER_VARIANTS
  // controller or AI to process actions queue
  actionPerformer: MinionActionPerformer
  // flag for minion when is chasing a candy (since there is no action for this and it's just a GoTo aciton)
  chasingCandy: boolean
  // action the minion is currently performing
  currAction?: CharacterAction
  // last previous action performed
  prevAction?: CharacterAction
  // flag used to freeze the minion, usually when is target of a spell until the spell casting finishes
  invalidate: boolean
  constructor(obj: Object3D) {
    super(obj)
    this.types.push(GAME_OBJECTS.MINION)
    this.unsub = () => false
    this.actions = []
    this.plot = obj.userData.coords
    this.actionPerformer = new MinionActionPerformer(this)
    this.chasingCandy = false
    this.invalidate = false
    this.mutation = CHARACTER_VARIANTS.REGULAR
  }

  receiveHit(action: ActionHitReceived) {
    // console.log('HIT')

    // notice that in case of spells there's no dealer to fight back
    if (action.dealer.uuid && !this.isInvalidated) {
      // attacked by minion and able to fight back
      this.actionPerformer.enqueueAttackAction(action.dealer.uuid, 3)
    }
    const nextHP = Math.max(this.hp - action.power, 0)

    if (nextHP === 0 && this.isAlive) {
      this.isDead = true
      // console.log('IM DEAD')

      // remove invalidation so minion can process the Die action
      if (this.invalidate) {
        this.invalidate = false
      }

      Characters.updateState(this.uuid, { state: CHARACTER_STATES.DEAD })
      this.clearActions()

      this.instantAction(new ActionDie())

      // CANDY THROW
      const potentialDestinations = getRangeBySight(2, this.coords)
      const freeDestinations = potentialDestinations.filter(d => Terrain.getElementAt(d) === null)
      if (freeDestinations.length) {
        const destination = freeDestinations[Math.floor(Math.random() * freeDestinations.length)]
        const myCandy = getGameElementByUUID(`candy_for_${destination.x}_${destination.y}`) as Candy
        myCandy.throwToFieldFrom(this.coords)
      }
    }

    this.hp = nextHP
  }
  onStart(): void {
    // subscribe for TICK events
    const unsub = addEventListener(EVENTS_TICK.TICK_QUARTER, ({ payload: tickDivision }) => {
      const obj = this.obj

      // subscribed for 1/4 of TICK but discard if is not current element tick time
      // more than one tick devision increase reaction speed x2 since that character will do action more than 1 per tick
      if (!obj?.userData.tickDivisions.includes(tickDivision)) return false

      this.actionLoop()
    })
    this.unsub = unsub

    // @TODO move it out of here

    // if (this.obj.userData.config.variant !== CHARACTER_VARIANTS.REGULAR) {
    //   this.mutate(this.obj.userData.config.variant)
    // }
  }

  get isInvalidated() {
    return (
      !this.isAlive || this.invalidate || [CHARACTER_STATES.CHEERING, CHARACTER_STATES.INACTIVE].includes(this.state)
    )
  }
  actionLoop() {
    // do not act if it's invalidated
    if (this.isInvalidated) return false

    const hasActions = this.actions.length > 0

    if (!hasActions || !this.actions[0].mandatory) {
      // no action or next is not mandatory
      const [candy, enemy] = this.scoutForTargets()

      if (candy && !enemy) {
        // has candy at sight but no enemy
        this.actionsForCandy(candy as Candy)
      } else if (!candy && enemy) {
        // has enemy at sight but no candy
        this.actionsForEnemy(enemy as Minion)
      } else if (candy && enemy) {
        // has enemy and candy at sight, choose 50/50
        Math.random() > 0.5 ? this.actionsForCandy(candy as Candy) : this.actionsForEnemy(enemy as Minion)
      } else if (!hasActions) {
        // no candy, no enemy, no actions
        // @todo Check if can loot, otherwise just move randomly
        this.loot()
      } else {
        // has actions, next is not mandatory, there's no object at sight, then process the next action
      }
    } else {
      // has actions, next is not mandatory
    }
    this.processNextAction()
  }

  actionsForCandy(candy: Candy) {
    this.removeFollowingMoveActions()
    this.preppendActions([new ActionGoTo({ coords: candy.coords, mandatory: true })])
    // this.actions = [new ActionGoTo({ coords: candy.coords }), ...this.actions]
    // this.chasingCandy = true
  }

  actionsForEnemy(enemy: Minion) {
    const nextAction = this.actions[0]
    const lastAction = this.actions.at(this.actions.length - 1)
    const nextActionIsLessRelevant =
      nextAction?.type && ![CHARACTER_ACTION.ATTACKTO, CHARACTER_ACTION.ATTACK].includes(nextAction.type)
    const isNotAttackingTo =
      lastAction?.type !== CHARACTER_ACTION.ATTACKTO && nextAction?.type !== CHARACTER_ACTION.ATTACK
    if (nextActionIsLessRelevant && isNotAttackingTo) {
      // console.log('proactive attack to', this.actions)
      this.setActions([new ActionAttackTo({ uuid: enemy.uuid, mandatory: true })])
    }
  }

  scoutForTargets() {
    const range = getRangeBySight(3, this.coords)
    const objectsInRange = getObjectsInRange([GAME_OBJECTS.MINION, GAME_OBJECTS.CANDY], range)
    const candies = objectsInRange.filter(o => o.types.includes(GAME_OBJECTS.CANDY))
    const aliveEnemies = objectsInRange
      .filter(o => o.types.includes(GAME_OBJECTS.MINION))
      .filter(o => {
        const m = o as Minion
        const notBurried = ![CHARACTER_STATES.BURIED].includes(m.state)
        return m.owner !== this.owner && m.owner !== COMMANDERS.NOBODY && !m.isInvalidated && notBurried
      })
    return [candies[0] ?? null, aliveEnemies[0] ?? null]
  }

  // checkScoutArea() {
  //   // skip proactive move if current action is mandatory (since was given by player)
  //   if (this.currAction?.mandatory) {
  //     // console.log('scout discarded by mandatory action')
  //     return false
  //   }
  //   // skip dead minion
  //   const iAmAlive =
  //     this.hp > 0 && ![CHARACTER_STATES.BURIED, CHARACTER_STATES.DEAD, CHARACTER_STATES.INACTIVE].includes(this.state)
  //   if (!iAmAlive) {
  //     // console.log('scout discarded by dead state')
  //     return false
  //   }

  //   const range = getRangeBySight(3, this.coords)
  //   // console.log('scount at', this.coords, range);

  //   const objectsInRange = getObjectsInRange([GAME_OBJECTS.MINION, GAME_OBJECTS.CANDY], range)
  //   if (this.owner === COMMANDERS.PLAYER) {
  //     Debug.log(
  //       this.uuid,
  //       `chasingCandy: ${this.chasingCandy}`,
  //       // this.actions.reduce((r, a) => `${r} - ${a.type}`, ''),
  //     )
  //   }

  //   const candies = objectsInRange.filter(o => o.types.includes(GAME_OBJECTS.CANDY))
  //   const aliveEnemies = objectsInRange
  //     .filter(o => o.types.includes(GAME_OBJECTS.MINION))
  //     .filter(o => {
  //       const m = o as Minion
  //       return (
  //         m.owner !== this.owner &&
  //         m.owner !== COMMANDERS.NOBODY &&
  //         m.hp > 0 &&
  //         ![CHARACTER_STATES.BURIED, CHARACTER_STATES.DEAD, CHARACTER_STATES.INACTIVE].includes(m.state)
  //       )
  //     })

  //   // was chasing but there's no candy at sight
  //   if (this.chasingCandy && candies.length === 0) {
  //     this.chasingCandy = false
  //   }
  //   // prioritize candy chase over anything else, discard dead
  //   if (this.chasingCandy || !iAmAlive) {
  //     // console.log('UNABLE TO SCOUT', this.chasingCandy, iAmAlive)
  //     return false
  //   }

  //   const candyOrFight =
  //     candies.length > 0 && aliveEnemies.length === 0
  //       ? 'candy'
  //       : candies.length === 0 && aliveEnemies.length > 0
  //       ? 'fight'
  //       : candies.length > 0 && aliveEnemies.length > 0
  //       ? Math.random() > 0.5
  //         ? 'fight'
  //         : 'candy'
  //       : ''

  //   if (candyOrFight === 'candy') {
  //     const candy = candies[0] as Candy
  //     this.removeFollowingMoveActions()
  //     this.preppendActions([new ActionGoTo({ coords: candy.coords })])
  //     // this.actions = [new ActionGoTo({ coords: candy.coords }), ...this.actions]
  //     // this.chasingCandy = true
  //   } else if (candyOrFight === 'fight') {
  //     const nextAction = this.actions[0]
  //     const lastAction = this.actions.at(this.actions.length - 1)
  //     const enemiesInRange = aliveEnemies.length > 0
  //     const nextActionIsLessRelevant =
  //       nextAction?.type && ![CHARACTER_ACTION.ATTACKTO, CHARACTER_ACTION.ATTACK].includes(nextAction.type)
  //     const isNotAttackingTo = lastAction?.type !== CHARACTER_ACTION.ATTACKTO
  //     if (enemiesInRange && nextActionIsLessRelevant && isNotAttackingTo) {
  //       // console.log('proactive attack to', this.actions)
  //       this.setActions([new ActionAttackTo({ uuid: aliveEnemies[0].uuid })])
  //     }
  //   }
  // }

  getNextAction() {
    const fallbackAction = new ActionAwait() // this.hp > 0 ? new ActionAwait() : new ActionInvalid()
    return this.actions.length ? this.actions.shift() || fallbackAction : fallbackAction
  }

  processNextAction() {
    this.prevAction = this.currAction
    const nextAction = this.getNextAction()
    this.currAction = nextAction

    this.performAction(nextAction)
    this.prevAction = nextAction
  }

  // removes Move actions from queue until next action that is not a Move
  removeFollowingMoveActions() {
    // removes all queued moves until some action that is not a move
    while (this.actions.length && this.actions[0] && this.actions[0].type === CHARACTER_ACTION.MOVE) {
      this.actions.shift()
    }
  }

  instantAction(action: CharacterAction) {
    this.setActions([action, ...this.actions])
    // this.actions = [action, ...this.actions]
    this.processNextAction()
  }
  performAction(currentAction: CharacterAction) {
    this.actionPerformer.performAction(currentAction)
  }

  setActions(actions: CharacterAction[]) {
    const isACommand = actions[0]?.mandatory
    if (isACommand) {
      this.chasingCandy = false
    }
    if (!this.invalidate) {
      this.actions = actions
    }
  }

  clearActions() {
    this.setActions([])
  }

  appendActions(actions: CharacterAction[]) {
    this.setActions([...this.actions, ...actions])
    // this.actions.push(...actions)
  }

  preppendActions(actions: CharacterAction[]) {
    this.setActions([...actions, ...this.actions])
    // this.actions.unshift(...(actions || []))
  }

  isSelectable() {
    const isMyUnit = this.owner === COMMANDERS.PLAYER
    const isActive = ![CHARACTER_STATES.INACTIVE, CHARACTER_STATES.BURIED].includes(
      this.state || CHARACTER_STATES.INACTIVE,
    )

    return isMyUnit && isActive
  }

  canInteract() {
    const isMyUnit = this.owner === COMMANDERS.PLAYER
    return !isMyUnit
  }

  onSelect() {
    const isSelected = Characters.isSelected(this.uuid)
    if (this.isSelectable() && !isSelected) {
      Characters.selectCharacter(this.uuid)
    } else if (this.isSelectable() && isSelected) {
      Characters.clearCharacterSelection()
    } else {
      super.onSelect()
    }
  }

  fixMutation() {
    this.restoreMutation && this.restoreMutation(this.obj)
    this.mutation = CHARACTER_VARIANTS.REGULAR
    this.restoreTeam()
  }
  mutate(variant: CHARACTER_VARIANTS) {
    this.fixMutation()

    switch (variant) {
      case CHARACTER_VARIANTS.DIABLO:
        replaceRedMatCapMaterial(this.obj)
        this.restoreMutation = restoreFromRedMatCapMaterials

        break
      case CHARACTER_VARIANTS.STATUE:
        replaceAsphaltMaterial(this.obj)
        this.animator.stop()
        this.restoreMutation = restoreFromAsphaltMaterials

        break
      case CHARACTER_VARIANTS.CANDY_ARMORED:
        this.weapon = WEAPONS.CANDY_SWORD
        this.restoreMutation = () => {
          this.weapon = undefined
        }
        break
      case CHARACTER_VARIANTS.REGULAR:
      default:
        break
    }

    this.mutation = variant
  }

  onUnload(): void {
    this.unsub()
  }

  teamWon() {
    this.actions = []
    this.animator.playCheer()
  }
  teamLost() {
    this.actions = []
    this.animator.idle()
  }
  loot() {
    const treasure = getEnemyTreasure(this.owner!)
    this.appendActions([new ActionLoot({ coords: treasure.coords })])
  }
  freeze() {
    this.invalidate = true
    this.animator.idle()
  }
  unfreeze() {
    this.invalidate = false
  }

  changeTeam(newCommander: COMMANDERS) {
    this.owner = newCommander
  }
  restoreTeam() {
    this.owner = this.prevOwner ?? this.owner
  }
}
